import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';

const cardStyle = { background: '#fff', border: '1px solid #e5e5e5', borderRadius: 10 };

// Editable text/textarea profile fields — must stay a subset of the BE's
// MEDIATOR_PROFILE_WRITABLE_COLUMNS whitelist (mediatorController.js).
// Mirrors Scale's Profile card exactly: First name, Last name, Email (read-only),
// Designation (display-only, rendered separately). Nothing else lives here.
const PROFILE_FIELDS = [
  { key: 'first_name', label: 'First name', type: 'text', required: true, half: true },
  { key: 'last_name', label: 'Last name', type: 'text', half: true },
  // Read-only, matching Scale: email is the mediator's login identity (auth.users).
  { key: 'email', label: 'Email', type: 'email', half: true, readOnly: true },
];

// Scalar (string) fields tracked in the form — everything that is NOT an
// array and NOT a boolean. Every one is string-coerced on load so a
// JSON/number/time column can never reach a .trim() call and crash the tab.
const SCALAR_KEYS = [
  ...PROFILE_FIELDS.map((f) => f.key),
  'timezone', 'morning_time', 'afternoon_time', 'fullday_time',
  'gsc_morning_time', 'gsc_afternoon_time', 'gsc_fullday_time',
  'invoice_footer', 'cancellation_policy', 'additional_information',
];

// Boolean fields — the per-slot "session offered" toggles. Stored and sent as
// true/false; NEVER String()/trim()'d (that crashed the tab historically).
// Default true when the BE returns null/undefined.
const BOOLEAN_KEYS = ['show_morning', 'show_afternoon', 'show_fullday'];

// Array (text[]) fields — the email CC recipient lists. Rendered as chips,
// always stored/sent as arrays of clean strings.
const ARRAY_KEYS = ['email_cc', 'email_cc_mediators'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Timezone options — mirrors the Scale portal's MediationTime.jsx list so
// the values written match what the booking pages expect.
const TIMEZONES = [
  { value: 'US/Eastern', label: 'Eastern Standard Time (EST)' },
  { value: 'US/Pacific', label: 'Pacific Standard Time (PST)' },
  { value: 'US/Central', label: 'Central Time (CT)' },
  { value: 'US/Mountain', label: 'Mountain Time (MST)' },
  { value: 'HST', label: 'Hawaii Time (HST)' },
];

// Build 'HH:MM:SS' -> "h:mm AM/PM" time slots at a 15-min interval, mirroring
// MediationTime.jsx (morning 0-12, afternoon 12-24, full day 0-24).
function generateTimeSlots(startHour, endHour) {
  const slots = [];
  for (let hour = startHour; hour < endHour; hour++) {
    for (let minutes = 0; minutes < 60; minutes += 15) {
      const label = new Date(0, 0, 0, hour, minutes, 0).toLocaleTimeString([], {
        hour: 'numeric', minute: '2-digit', hour12: true,
      });
      const value = `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
      slots.push({ value, label });
    }
  }
  return slots;
}
const MORNING_SLOTS = generateTimeSlots(0, 12);
const AFTERNOON_SLOTS = generateTimeSlots(12, 24);
const FULLDAY_SLOTS = generateTimeSlots(0, 24);

const TIME_FIELDS = [
  { key: 'morning_time', label: 'Morning slot', slots: MORNING_SLOTS, showKey: 'show_morning' },
  { key: 'afternoon_time', label: 'Afternoon slot', slots: AFTERNOON_SLOTS, showKey: 'show_afternoon' },
  { key: 'fullday_time', label: 'Full day slot', slots: FULLDAY_SLOTS, showKey: 'show_fullday' },
];

// GSC (General Session Coordinator) session start times — same slot grids,
// separate columns. Only rendered when the mediator is GSC-enabled.
const GSC_TIME_FIELDS = [
  { key: 'gsc_morning_time', label: 'Morning slot', slots: MORNING_SLOTS },
  { key: 'gsc_afternoon_time', label: 'Afternoon slot', slots: AFTERNOON_SLOTS },
  { key: 'gsc_fullday_time', label: 'Full day slot', slots: FULLDAY_SLOTS },
];

const EMAIL_CC_FIELDS = [
  { key: 'email_cc', label: 'All Email CC', hint: 'These addresses are CC’d on this mediator’s client emails.' },
  { key: 'email_cc_mediators', label: 'Mediator CC Emails', hint: 'These addresses are CC’d on mediator-facing emails.' },
];

// Coerce any incoming value into a clean string[] (arrays, comma/newline
// strings, or nullish all handled) — never throws, mirrors the BE.
function ensureArray(value) {
  let list;
  if (Array.isArray(value)) list = value;
  else if (typeof value === 'string') list = value.split(/[,\n]/);
  else return [];
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const s = (typeof item === 'string' ? item : String(item ?? '')).trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function emptyForm() {
  const base = SCALAR_KEYS.reduce((acc, k) => ({ ...acc, [k]: '' }), {});
  const withArrays = ARRAY_KEYS.reduce((acc, k) => ({ ...acc, [k]: [] }), base);
  // Booleans default ON, mirroring the BE's default-true "session offered".
  return BOOLEAN_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), withArrays);
}

const labelStyle = {
  display: 'block', fontFamily: 'Inter, sans-serif', fontSize: 12,
  fontWeight: 500, color: '#374151', marginBottom: 6,
};
function fieldStyle(err) {
  return {
    width: '100%', fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#0e1e20',
    background: '#fff', border: `1px solid ${err ? '#dc2626' : '#e5e5e5'}`,
    borderRadius: 8, padding: '9px 12px', outline: 'none',
  };
}

// Chip/tag editor for a text[] email list. Value is always an array; adds on
// Enter/comma/blur, removes via the × on each chip.
function EmailTagInput({ value, onChange, disabled }) {
  const [draft, setDraft] = useState('');
  const list = Array.isArray(value) ? value : [];

  const commit = (raw) => {
    const s = String(raw ?? '').trim().replace(/,$/, '').trim();
    if (!s) return;
    if (!list.includes(s)) onChange([...list, s]);
    setDraft('');
  };
  const removeAt = (idx) => onChange(list.filter((_, i) => i !== idx));

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && !draft && list.length) {
      removeAt(list.length - 1);
    }
  };

  return (
    <div
      style={{
        display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
        border: '1px solid #e5e5e5', borderRadius: 8, padding: '6px 8px',
        background: disabled ? '#f9fafb' : '#fff',
      }}
    >
      {list.map((email, idx) => {
        const invalid = !EMAIL_RE.test(email);
        return (
          <span
            key={`${email}-${idx}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontFamily: 'Inter, sans-serif', fontSize: 12,
              background: invalid ? '#fef2f2' : '#f0fdfa',
              color: invalid ? '#dc2626' : '#115e59',
              border: `1px solid ${invalid ? '#fecaca' : '#99f6e4'}`,
              borderRadius: 6, padding: '3px 6px 3px 8px',
            }}
            title={invalid ? 'This does not look like a valid email' : email}
          >
            {email}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeAt(idx)}
                style={{
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  color: 'inherit', fontSize: 14, lineHeight: 1, padding: 0,
                }}
                aria-label={`Remove ${email}`}
              >
                ×
              </button>
            )}
          </span>
        );
      })}
      <input
        type="text"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(draft)}
        placeholder={list.length ? '' : 'Add email and press Enter'}
        style={{
          flex: 1, minWidth: 140, border: 'none', outline: 'none',
          fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#0e1e20',
          background: 'transparent', padding: '3px 2px',
        }}
      />
    </div>
  );
}

// Small on/off pill toggle — mirrors Scale's "session offered" switch.
function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        position: 'relative', width: 38, height: 22, flexShrink: 0,
        borderRadius: 999, border: 'none', padding: 0,
        background: checked ? '#115e59' : '#d1d5db',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1, transition: 'background 0.15s',
      }}
    >
      <span
        style={{
          position: 'absolute', top: 2, left: checked ? 18 : 2,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div style={cardStyle}>
      <div className="px-5 pt-4 pb-3 border-b border-[#e5e5e5]">
        <h3 className="text-[#0e1e20] text-sm font-semibold" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
          {title}
        </h3>
        {subtitle && (
          <p className="text-[#5b5a64] text-[12px] mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
            {subtitle}
          </p>
        )}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function ProfileTab({ mediatorId, notify, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [initial, setInitial] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  // READ-ONLY: gates the GSC Mediation Times card. Never sent back to the BE.
  const [isGscAllowed, setIsGscAllowed] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!mediatorId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(`/mediators/${mediatorId}/profile`);
      const data = res.data?.data || {};
      // Coerce scalars to strings (a JSON/number/time column would otherwise
      // reach the inputs and crash the .trim() calls) and arrays to clean
      // string lists.
      const next = emptyForm();
      SCALAR_KEYS.forEach((k) => {
        const v = data[k];
        next[k] = v == null ? '' : typeof v === 'string' ? v : String(v);
      });
      ARRAY_KEYS.forEach((k) => {
        next[k] = ensureArray(data[k]);
      });
      // Booleans: default true when null/undefined; never string-coerced.
      BOOLEAN_KEYS.forEach((k) => {
        next[k] = data[k] == null ? true : Boolean(data[k]);
      });
      setForm(next);
      setInitial(next);
      setIsGscAllowed(data.is_gsc_cases_allowed === true);
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  }, [mediatorId, notify]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const isDirty = useMemo(() => {
    const scalarDirty = SCALAR_KEYS.some(
      (k) => String(form[k] ?? '').trim() !== String(initial[k] ?? '').trim()
    );
    const arrayDirty = ARRAY_KEYS.some(
      (k) => JSON.stringify(ensureArray(form[k])) !== JSON.stringify(ensureArray(initial[k]))
    );
    const booleanDirty = BOOLEAN_KEYS.some(
      (k) => Boolean(form[k]) !== Boolean(initial[k])
    );
    return scalarDirty || arrayDirty || booleanDirty;
  }, [form, initial]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!String(form.first_name ?? '').trim()) next.first_name = 'First name is required';
    const emailVal = String(form.email ?? '').trim();
    if (emailVal && !EMAIL_RE.test(emailVal)) next.email = 'Enter a valid email';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    // Send only changed fields. Scalars trimmed; arrays sent as clean lists.
    const payload = {};
    SCALAR_KEYS.forEach((k) => {
      const v = String(form[k] ?? '').trim();
      if (v !== String(initial[k] ?? '').trim()) payload[k] = v;
    });
    ARRAY_KEYS.forEach((k) => {
      const cur = ensureArray(form[k]);
      if (JSON.stringify(cur) !== JSON.stringify(ensureArray(initial[k]))) payload[k] = cur;
    });
    // Booleans compared/sent as true/false — no coercion.
    BOOLEAN_KEYS.forEach((k) => {
      const cur = Boolean(form[k]);
      if (cur !== Boolean(initial[k])) payload[k] = cur;
    });
    if (Object.keys(payload).length === 0) return;
    try {
      setSaving(true);
      await api.put(`/mediators/${mediatorId}/profile`, payload);
      notify?.({ type: 'success', message: 'Profile updated' });
      await fetchProfile();
      onSaved?.();
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(initial);
    setErrors({});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ ...cardStyle, padding: '48px 0' }}>
        <div className="w-6 h-6 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const selectStyle = { ...fieldStyle(false), appearance: 'auto' };

  return (
    <div className="flex flex-col gap-4">
      {/* ---- Profile ---- */}
      <SectionCard title="Profile" subtitle="View and edit this mediator’s contact details and public profile.">
        <div className="grid grid-cols-2 gap-4">
          {PROFILE_FIELDS.map((f) => {
            const err = errors[f.key];
            return (
              <div key={f.key} className={f.half ? 'col-span-2 md:col-span-1' : 'col-span-2'}>
                <label style={labelStyle}>
                  {f.label}
                  {f.required && <span style={{ color: '#dc2626' }}> *</span>}
                </label>
                {f.type === 'textarea' ? (
                  <textarea
                    value={form[f.key]}
                    onChange={(e) => handleChange(f.key, e.target.value)}
                    disabled={saving}
                    rows={4}
                    style={{ ...fieldStyle(err), resize: 'vertical' }}
                  />
                ) : (
                  <input
                    type={f.type}
                    value={form[f.key]}
                    onChange={(e) => handleChange(f.key, e.target.value)}
                    disabled={saving || f.readOnly}
                    readOnly={f.readOnly}
                    style={f.readOnly
                      ? { ...fieldStyle(false), background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }
                      : fieldStyle(err)}
                  />
                )}
                {err && (
                  <span className="text-[11px]" style={{ color: '#dc2626', fontFamily: 'Inter, sans-serif' }}>{err}</span>
                )}
              </div>
            );
          })}

          {/* Designation — display only. Scale hardcodes "Mediator"; never sent. */}
          <div className="col-span-2 md:col-span-1">
            <label style={labelStyle}>Designation</label>
            <input
              type="text"
              value="Mediator"
              disabled
              readOnly
              style={{ ...fieldStyle(false), background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }}
            />
          </div>
        </div>
      </SectionCard>

      {/* ---- Mediation Times ---- */}
      <SectionCard title="Mediation Times" subtitle="Default session start times and timezone shown on this mediator’s booking pages.">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 md:col-span-1">
            <label style={labelStyle}>Timezone</label>
            <select
              value={form.timezone || ''}
              onChange={(e) => handleChange('timezone', e.target.value)}
              disabled={saving}
              style={selectStyle}
            >
              <option value="">Select timezone…</option>
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
              {/* Preserve an unrecognised stored value rather than dropping it. */}
              {form.timezone && !TIMEZONES.some((tz) => tz.value === form.timezone) && (
                <option value={form.timezone}>{form.timezone}</option>
              )}
            </select>
          </div>

          {TIME_FIELDS.map((f) => {
            const enabled = Boolean(form[f.showKey]);
            return (
              <div key={f.key} className="col-span-2 md:col-span-1">
                <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>{f.label}</label>
                  <Toggle
                    checked={enabled}
                    onChange={(next) => handleChange(f.showKey, next)}
                    disabled={saving}
                    label={`Offer ${f.label}`}
                  />
                </div>
                <select
                  value={form[f.key] || ''}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  disabled={saving || !enabled}
                  style={{
                    ...selectStyle,
                    background: enabled ? '#fff' : '#f9fafb',
                    color: enabled ? '#0e1e20' : '#9ca3af',
                    cursor: enabled ? 'auto' : 'not-allowed',
                  }}
                >
                  <option value="">Not set</option>
                  {f.slots.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                  {/* Keep an off-grid stored time selectable. */}
                  {form[f.key] && !f.slots.some((s) => s.value === form[f.key]) && (
                    <option value={form[f.key]}>{form[f.key]}</option>
                  )}
                </select>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* ---- GSC Mediation Times (only for GSC-enabled mediators) ---- */}
      {isGscAllowed && (
        <SectionCard title="GSC Mediation Times" subtitle="Default session start times used for this mediator’s GSC cases.">
          <div className="grid grid-cols-2 gap-4">
            {GSC_TIME_FIELDS.map((f) => (
              <div key={f.key} className="col-span-2 md:col-span-1">
                <label style={labelStyle}>{f.label}</label>
                <select
                  value={form[f.key] || ''}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  disabled={saving}
                  style={selectStyle}
                >
                  <option value="">Not set</option>
                  {f.slots.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                  {/* Keep an off-grid stored time selectable. */}
                  {form[f.key] && !f.slots.some((s) => s.value === form[f.key]) && (
                    <option value={form[f.key]}>{form[f.key]}</option>
                  )}
                </select>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ---- Email CC ---- */}
      <SectionCard title="Email CC" subtitle="Recipients copied on this mediator’s outgoing emails. Press Enter or comma to add.">
        <div className="grid grid-cols-1 gap-4">
          {EMAIL_CC_FIELDS.map((f) => (
            <div key={f.key}>
              <label style={labelStyle}>{f.label}</label>
              <EmailTagInput
                value={form[f.key]}
                onChange={(next) => handleChange(f.key, next)}
                disabled={saving}
              />
              <span className="text-[11px] mt-1 block" style={{ color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}>
                {f.hint}
              </span>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* ---- Invoice Memo ---- */}
      <SectionCard title="Invoice Memo" subtitle="Footer text that appears at the bottom of this mediator’s invoices.">
        <div className="flex flex-col gap-4">
          <div>
            <label style={labelStyle}>Invoice Footer</label>
            <textarea
              value={form.invoice_footer}
              onChange={(e) => handleChange('invoice_footer', e.target.value)}
              disabled={saving}
              rows={4}
              placeholder="Enter invoice footer"
              style={{ ...fieldStyle(false), resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Cancelation Policy</label>
            <textarea
              value={form.cancellation_policy}
              onChange={(e) => handleChange('cancellation_policy', e.target.value)}
              disabled={saving}
              rows={4}
              placeholder="Enter cancelation policy"
              style={{ ...fieldStyle(false), resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Additional Information</label>
            <textarea
              value={form.additional_information}
              onChange={(e) => handleChange('additional_information', e.target.value)}
              disabled={saving}
              rows={4}
              placeholder="Enter additional information"
              style={{ ...fieldStyle(false), resize: 'vertical' }}
            />
          </div>
        </div>
      </SectionCard>

      {/* ---- Actions ---- */}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleReset}
          disabled={saving || !isDirty}
          style={{
            fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600,
            padding: '9px 16px', borderRadius: 8,
            border: '1px solid #e5e5e5', background: '#fff',
            color: '#5b5a64', cursor: saving || !isDirty ? 'not-allowed' : 'pointer',
            opacity: saving || !isDirty ? 0.5 : 1,
          }}
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !isDirty}
          style={{
            fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600,
            padding: '9px 18px', borderRadius: 8, border: 'none',
            background: saving || !isDirty ? '#e5e5e5' : '#115e59',
            color: saving || !isDirty ? '#9ca3af' : '#fff',
            cursor: saving || !isDirty ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
